import { PlusIcon } from "@heroicons/react/24/outline";

import { TableBuilderDynamic } from "@fronterahealth/frontera-ui-components";
import { JSONDatumInterface } from "@fronterahealth/frontera-ui-components/TableBuilderDynamic/TableBuilderDynamic";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { FieldError } from "@fronterahealth/frontera-ui-components/ui/field";

import {
  SAMPLE_TABLE_CONTENT,
  TableContentIssues,
  parseTableContent,
} from "@components/ReportField/inputs/tableContent";
import { ReportFieldInputProps } from "@components/ReportField/types";

const EMPTY_TABLE_CONTENT: JSONDatumInterface = { headers: [], data: [] };

export const TableContentErrors: React.FC<{ issues: TableContentIssues }> = ({ issues }) => (
  <FieldError errors={issues}>
    {issues.map((issue) => (
      <dl key={issue.path.join(".")} className="flex flex-row gap-1">
        <dt>[{issue.path.join(".")}]</dt>
        <dd>{issue.message}</dd>
      </dl>
    ))}
  </FieldError>
);

export const ReportFieldTable: React.FC<ReportFieldInputProps> = ({ field, variant, value, onChange, disabled }) => {
  const result = parseTableContent(value, EMPTY_TABLE_CONTENT);

  if (!result.success) return <TableContentErrors issues={result.issues} />;

  const jsonDatum = result.data;

  if (!jsonDatum.headers.length) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <p className="text-sm text-text-secondary">No existing data. Add a table to get started.</p>
        <Button data-testid="add-table-sample" onClick={() => onChange(JSON.stringify(SAMPLE_TABLE_CONTENT))}>
          <PlusIcon /> Add table sample
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        // Wide tables (e.g. Vineland Age Equivalence: 7 columns) must scroll
        // horizontally within this wrapper instead of overflowing the page
        // (PROA-2316). Unconditional -- overflow is a layout concern, not an
        // edit-permission one.
        "overflow-x-auto",
        !disabled && "transition-[padding,margin] delay-150 motion-reduce:delay-0",
        !disabled && variant !== "ghost" && "pr-8",
        !disabled && variant === "ghost" && "pr-0 group-hover/field:pr-8",
        !disabled && "-mt-10 group-hover/field:mt-0",
        !disabled && "-mb-9 group-hover/field:mb-0",
        disabled && "-mt-10",
      )}
    >
      <TableBuilderDynamic
        tableSaving={false}
        displayOnlyMode={disabled}
        title=""
        tableId={field.id}
        jsonDatum={jsonDatum}
        onSave={async (datum) => {
          await onChange(JSON.stringify(datum));
        }}
      />
    </div>
  );
};
